import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useHousehold from '../../hooks/useHousehold';
import { ROLES } from '../../constants/roles';
import { authFetch } from '../../lib/authFetch';
import { useFormatting } from '../../context/FormattingContext';
import { getTodayDateKeyInTimeZone } from '../../utils/formatting';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';

type AccessScope = 'private' | 'household';
type AccountType = 'checking' | 'savings' | 'creditCard';
type AccountCategory = 'asset' | 'liability';

interface Account {
  _id: string;
  name: string;
  accountType: string;
  accountCategory: string;
  accessScope?: AccessScope;
}

interface OpeningBalanceUpdate {
  accountId: string;
  openingBalance: number;
}

const ONBOARDING_NAMESPACE = 'onboarding-ledger-v1';

const normalizeMessage = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message;
  return String(value);
};

const parseNumberInput = (raw: string): number | null => {
  if (!raw.trim()) return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
};

const OnboardingForm: React.FC = () => {
  const navigate = useNavigate();
  const { auth, refreshUser } = useAuth();
  const formatting = useFormatting();
  const household = useHousehold();

  useEffect(() => {
    document.title = 'Get Started | Velora Money';
  }, []);

  const storageScope = auth.user?.id || 'anonymous';
  const createdStorageKey = `${ONBOARDING_NAMESPACE}:${storageScope}:createdAccountIds`;

  const [createdAccountIds, setCreatedAccountIds] = useState<string[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState('');
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountType, setNewAccountType] = useState<AccountType>('checking');
  const [newAccountCategory, setNewAccountCategory] = useState<AccountCategory>('asset');
  const [newAccessScope, setNewAccessScope] = useState<AccessScope>('private');
  const [startingBalances, setStartingBalances] = useState<Record<string, string>>({});
  const [baselineErrors, setBaselineErrors] = useState<Record<string, string>>({});
  const [accountsSectionError, setAccountsSectionError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const token = auth.token || localStorage.getItem('token');
  const role = auth.user?.role;
  const canCreateAccounts = role !== ROLES.CHILD;
  const canCreateSharedAccounts = canCreateAccounts && !household.loading && !household.isEmpty && !household.error;

  useEffect(() => {
    const rawCreatedIds = localStorage.getItem(createdStorageKey);
    if (!rawCreatedIds) {
      setCreatedAccountIds([]);
      return;
    }

    try {
      const parsed = JSON.parse(rawCreatedIds);
      if (Array.isArray(parsed)) {
        setCreatedAccountIds(parsed.filter((value): value is string => typeof value === 'string'));
      } else {
        setCreatedAccountIds([]);
      }
    } catch {
      setCreatedAccountIds([]);
    }
  }, [createdStorageKey]);

  useEffect(() => {
    localStorage.setItem(createdStorageKey, JSON.stringify(createdAccountIds));
  }, [createdAccountIds, createdStorageKey]);

  const loadAccounts = useCallback(async () => {
    if (!token) return;

    setAccountsLoading(true);
    setAccountsError('');
    try {
      const response = await authFetch('/api/accounts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json().catch(() => ([]));
      if (!response.ok) {
        throw new Error(normalizeMessage((payload as { message?: unknown })?.message) || 'Failed to load accounts.');
      }
      setAccounts(Array.isArray(payload) ? payload as Account[] : []);
    } catch (error) {
      setAccounts([]);
      setAccountsError(normalizeMessage(error) || 'Failed to load accounts.');
    } finally {
      setAccountsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const accountMap = useMemo(
    () => new Map(accounts.map((account) => [account._id, account])),
    [accounts]
  );
  const onboardingBaselineDate = useMemo(() => {
    const dateKey = getTodayDateKeyInTimeZone(formatting.timeZone);
    if (dateKey) return dateKey;
    return new Date().toISOString().slice(0, 10);
  }, [formatting.timeZone]);

  const completeOnboarding = useCallback(async () => {
    if (!token) throw new Error('You must be signed in to complete onboarding.');
    const response = await authFetch('/api/users/complete-onboarding', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const payload = await response.json().catch(() => ({} as { message?: string }));
    if (!response.ok) {
      throw new Error(payload.message || 'Unable to complete onboarding right now. Please try again.');
    }
  }, [token]);

  const buildOpeningBalanceUpdates = useCallback(
    (): { updates: OpeningBalanceUpdate[]; errors: Record<string, string> } => {
    const nextErrors: Record<string, string> = {};

    const updates = createdAccountIds
      .map((accountId) => {
        const account = accountMap.get(accountId);
        if (!account) return null;

        const balance = parseNumberInput(startingBalances[accountId] || '');
        if (balance === null || balance === 0) return null;

        if (account.accountCategory === 'liability') {
          if (balance <= 0) {
            nextErrors[accountId] = 'Credit card starting balances must be greater than 0 for onboarding v1.';
            return null;
          }
        }

        return {
          accountId,
          openingBalance: balance,
        } satisfies OpeningBalanceUpdate;
      })
      .filter((update): update is OpeningBalanceUpdate => Boolean(update))
      .sort((a, b) => a.accountId.localeCompare(b.accountId));

    return { updates, errors: nextErrors };
  }, [accountMap, createdAccountIds, startingBalances]);

  const applyOpeningBalanceUpdates = async (updates: OpeningBalanceUpdate[]) => {
    if (!token) throw new Error('You must be signed in to complete onboarding.');

    for (const update of updates) {
      const response = await authFetch(`/api/accounts/${update.accountId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          openingBalance: update.openingBalance,
          baselineDate: onboardingBaselineDate,
        }),
      });
      const payload = await response.json().catch(() => ({} as { message?: string }));
      if (!response.ok) {
        throw new Error(payload.message || 'Unable to save starting balances right now. Please try again.');
      }
    }
  };

  const handleCreateAccount = async () => {
    if (!canCreateAccounts) return;
    if (!token) {
      setAccountsSectionError('You must be signed in to create accounts.');
      return;
    }

    const name = newAccountName.trim();
    if (!name) {
      setAccountsSectionError('Account name is required.');
      return;
    }

    setIsCreatingAccount(true);
    setAccountsSectionError('');
    try {
      const response = await authFetch('/api/accounts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name,
          accountType: newAccountType,
          accountCategory: newAccountCategory,
          accessScope: canCreateSharedAccounts ? newAccessScope : 'private',
          baselineDate: onboardingBaselineDate,
        }),
      });

      const payload = await response.json().catch(() => ({} as { message?: string; account?: Account; _id?: string }));
      if (!response.ok) {
        throw new Error(payload.message || 'Failed to create account.');
      }

      const createdId = payload.account?._id || payload._id;
      if (typeof createdId === 'string' && createdId) {
        setCreatedAccountIds((prev) => (prev.includes(createdId) ? prev : [...prev, createdId]));
      }

      setNewAccountName('');
      setNewAccessScope('private');
      await loadAccounts();
    } catch (error) {
      setAccountsSectionError(normalizeMessage(error) || 'Failed to create account.');
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const handleSubmit = async () => {
    setErrorMessage('');
    setBaselineErrors({});
    setIsCompleting(true);
    try {
      const { updates, errors } = buildOpeningBalanceUpdates();
      setBaselineErrors(errors);
      if (Object.keys(errors).length > 0) {
        throw new Error('Fix starting balance errors before completing onboarding.');
      }

      if (updates.length > 0) {
        await applyOpeningBalanceUpdates(updates);
      }

      await completeOnboarding();
      localStorage.removeItem(createdStorageKey);
      setCreatedAccountIds([]);
      setStartingBalances({});
      await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      setErrorMessage(normalizeMessage(error) || 'Unable to complete onboarding right now. Please try again.');
    } finally {
      setIsCompleting(false);
    }
  };

  const sharingHelperText = canCreateAccounts && household.isEmpty
    ? 'Household sharing is configured during setup. If you need it enabled, please contact support.'
    : '';

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={{ xs: 2.5, md: 3 }}>
        <Paper
          sx={{
            p: { xs: 2.5, sm: 3, md: 4 },
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(255, 255, 255, 0.98) 55%, rgba(46, 125, 50, 0.08) 100%)',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 18px 40px rgba(15, 23, 42, 0.06)',
          }}
        >
          <Stack spacing={1.5}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.75,
                borderRadius: 999,
                bgcolor: 'rgba(25, 118, 210, 0.10)',
                color: 'primary.main',
                width: 'fit-content',
              }}
            >
              <DirectionsRunIcon fontSize="small" />
              <Typography variant="body2" fontWeight={700}>
                Guided setup
              </Typography>
            </Box>
            <Typography
              variant="h4"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, letterSpacing: '-0.02em' }}
            >
              Set up your accounts and current balances
            </Typography>
            <Stack spacing={0.75} sx={{ maxWidth: 720 }}>
              <Typography variant="body1">
                Add the accounts you want to track, then enter each account&apos;s current balance as of today.
              </Typography>
              <Typography variant="body1" color="text.secondary">
                This gives Velora a clear starting point for future tracking.
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        {!canCreateAccounts && (
          <Alert severity="info">
            Child profiles cannot create accounts or set starting balances during onboarding.
          </Alert>
        )}

        <Paper
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                Step 1
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.25, mb: 0.75, fontWeight: 700 }}>
                Create Accounts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add the checking, savings, and credit card accounts you actively use.
              </Typography>
            </Box>

            <Stack spacing={1.5}>
              <TextField
                label="Account name"
                size="small"
                fullWidth
                value={newAccountName}
                disabled={!canCreateAccounts || isCreatingAccount}
                onChange={(event) => setNewAccountName(event.target.value)}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Account type"
                  value={newAccountType}
                  disabled={!canCreateAccounts || isCreatingAccount}
                  onChange={(event) => {
                    const nextType = event.target.value as AccountType;
                    setNewAccountType(nextType);
                    setNewAccountCategory(nextType === 'creditCard' ? 'liability' : 'asset');
                  }}
                >
                  <MenuItem value="checking">Checking</MenuItem>
                  <MenuItem value="savings">Savings</MenuItem>
                  <MenuItem value="creditCard">Credit Card</MenuItem>
                </TextField>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Account category"
                  value={newAccountCategory}
                  disabled={!canCreateAccounts || isCreatingAccount}
                  onChange={(event) => setNewAccountCategory(event.target.value as AccountCategory)}
                >
                  <MenuItem value="asset">Asset</MenuItem>
                  <MenuItem value="liability">Liability</MenuItem>
                </TextField>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                This helps organize the account behind the scenes. In most cases, the default option is the right one.
              </Typography>
              {newAccountType === 'creditCard' && (
                <Typography variant="body2" color="text.secondary">
                  You can finish credit card details like limit, statement close day, and due day right after onboarding.
                </Typography>
              )}

              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: { xs: 1.5, sm: 1.75 },
                  bgcolor: 'background.default',
                }}
              >
                <FormControl component="fieldset" fullWidth disabled={!canCreateAccounts || isCreatingAccount}>
                  <FormLabel component="legend">Access scope</FormLabel>
                  <RadioGroup
                    value={newAccessScope}
                    onChange={(event) => setNewAccessScope(event.target.value as AccessScope)}
                  >
                    <FormControlLabel value="private" control={<Radio />} label="Private (only me)" />
                    {canCreateSharedAccounts && (
                      <FormControlLabel
                        value="household"
                        control={<Radio />}
                        label="Shared with household"
                      />
                    )}
                  </RadioGroup>
                </FormControl>
                {sharingHelperText && (
                  <Typography variant="body2" color="text.secondary">
                    {sharingHelperText}
                  </Typography>
                )}
              </Box>

              <Button
                variant="contained"
                onClick={handleCreateAccount}
                disabled={!canCreateAccounts || isCreatingAccount}
                startIcon={isCreatingAccount ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                Create account
              </Button>

              {accountsSectionError && <Alert severity="error">{accountsSectionError}</Alert>}
            </Stack>
          </Stack>
        </Paper>

        <Paper
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                Step 2
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.25, mb: 0.75, fontWeight: 700 }}>
                Enter each account&apos;s current balance as of today
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  These balances become the starting point for your accounts and future tracking.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  They are treated as account baselines, not as new income or expense transactions.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Current balances can only be entered for accounts created in this onboarding session.
                </Typography>
              </Stack>
            </Box>

            {accountsLoading ? (
              <Typography color="text.secondary">Loading accounts...</Typography>
            ) : accountsError ? (
              <Alert severity="error">{accountsError}</Alert>
            ) : accounts.length === 0 ? (
              <Typography color="text.secondary">No accounts yet. Create an account to set a starting balance.</Typography>
            ) : (
              <Stack spacing={1.5}>
                {accounts.map((account) => {
                  const isCreatedInSession = createdAccountIds.includes(account._id);
                  const accountTypeLabel = account.accountType || account.accountCategory;
                  const accessLabel = account.accessScope === 'household' ? 'Shared' : 'Private';
                  const balanceGuidance = account.accountCategory === 'liability'
                    ? 'Enter the current amount owed as of today. Use zero or a positive number.'
                    : 'Enter the current balance as of today. Negative is okay if the account is overdrawn.';
                  const helperMessage = baselineErrors[account._id]
                    || (isCreatedInSession ? balanceGuidance : '')
                    || (!canCreateAccounts
                      ? 'Children cannot set starting balances during onboarding.'
                      : !isCreatedInSession && account.accessScope === 'household'
                        ? 'View only during onboarding.'
                        : !isCreatedInSession
                          ? 'Only accounts created in this onboarding session can set a starting balance.'
                          : '');
                  return (
                    <Box
                      key={account._id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: { xs: 1.5, sm: 1.75 },
                        bgcolor: isCreatedInSession ? 'rgba(25, 118, 210, 0.03)' : 'background.paper',
                      }}
                    >
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography fontWeight={600}>{account.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {`${accountTypeLabel} • ${accessLabel}`}
                          </Typography>
                        </Box>
                        <TextField
                          type="number"
                          label={`Starting balance (${account.name})`}
                          size="small"
                          sx={{ minWidth: { md: 320 } }}
                          value={startingBalances[account._id] || ''}
                          onChange={(event) =>
                            setStartingBalances((prev) => ({ ...prev, [account._id]: event.target.value }))
                          }
                          disabled={!canCreateAccounts || !isCreatedInSession || isCompleting}
                          error={Boolean(baselineErrors[account._id])}
                          helperText={helperMessage}
                        />
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Paper>

        <Paper
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(180deg, rgba(46, 125, 50, 0.06) 0%, rgba(255, 255, 255, 1) 100%)',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" color="success.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                Step 3
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.25, mb: 0.75, fontWeight: 700 }}>
                Complete onboarding
              </Typography>
              <Stack spacing={0.75}>
                <Typography variant="body2" color="text.secondary">
                  Complete setup to start with an accurate dashboard based on your current account balances.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add recurring bills and income next from Transactions so your future cash flow is more accurate.
                </Typography>
              </Stack>
            </Box>

            <Box>
              <Button
                variant="contained"
                color="success"
                onClick={handleSubmit}
                disabled={isCompleting}
                startIcon={isCompleting ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                🚀 Complete onboarding
              </Button>
            </Box>

            {errorMessage && (
              <Alert severity="error">
                {errorMessage}
              </Alert>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
};

export default OnboardingForm;
